import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Switch from '@mui/material/Switch';
import { useTranslation } from 'react-i18next';

import { useConversationSettingsService } from './hooks';
import Button from 'atoms/Button';
import { useDispatch, useSelector } from 'react-redux';
import { getPreferredLanguageCode } from 'features/User/selectors';
import { getLanguageName, type Language } from 'utils/languages';
import { updateLanguagePreference } from 'features/User/actions';
import { LanguageSelectionModal } from '../../LanguageSelectionModal';

const Container = styled.div`
  ${({ theme: { colors, mq, utils } }) => css`
    display: grid;
    grid-auto-rows: min-content;
    row-gap: 1rem;

    ${mq.md.down} {
      & > div:not(:first-of-type) {
        position: relative;

        &::before {
          position: absolute;
          content: '';
          top: -0.5rem;
          left: 0;
          width: 100%;
          height: 1px;
          background-color: ${utils.alphaHex(colors.primary.main, 5)};
        }
      }
    }
  `}
`;

const Title = styled.h2`
  ${({ theme: { mq, tp } }) => css`
    ${tp.h3};

    margin: 0 0 1rem;

    ${mq.md.down} {
      display: none;
    }
  `}
`;

const SettingRow = styled.div`
  ${({ theme: { colors, durations, mq, utils } }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 3rem;
    transition: all ${durations.regular};

    ${mq.md.up} {
      background-color: ${colors.neutral.white};
      border-radius: 3.75rem;
    }

    ${mq.md.down} {
      padding: 2rem;
      margin: 0 -2rem;
      border-radius: 1rem;
    }
  `}
`;

const SettingLabel = styled.label`
  ${({ theme: { tp } }) => css`
    ${tp.p1};
    user-select: none;
  `}
`;

const StyledSwitch = styled(Switch)`
  ${({ theme: { colors } }) => css`
    & .MuiSwitch-switchBase {
      &.Mui-checked {
        color: ${colors.primary.light};
        
        & + .MuiSwitch-track {
          background-color: ${colors.primary.light};
          opacity: 1;
        }
      }
    }
  `}
`;

const ActionsContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: flex;
    align-items: center;
    column-gap: 1rem;

    ${mq.md.down} {
      column-gap: 0.75rem;
    }
  `}
`;

const LanguageValuePill = styled.span`
  ${({ theme: { colors, utils, tp } }) => css`
    ${tp.p2};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${utils.rem(6)} ${utils.rem(12)};
    min-width: ${utils.rem(80)};
    border-radius: 999rem;
    background-color: ${utils.alphaHex(colors.primary.main, 8)};
    color: ${colors.primary.main};
  `}
`;

const ChangeButton = styled(Button)`
  ${({ theme: { utils, mq } }) => css`
    padding: ${utils.rem(10)} ${utils.rem(20)};
    border-radius: 999rem;

    ${mq.md.down} {
      padding: ${utils.rem(9)} ${utils.rem(18)};
    }
  `}
`;

const ConversationSettings: React.FC = () => {
  const { studyInNativeLanguage, handleToggleChange } = useConversationSettingsService();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const preferredLanguageCode = useSelector(getPreferredLanguageCode);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

  const preferredLanguageName = useMemo(() => {
    if (!preferredLanguageCode) return t('common.notSet', 'Not set');
    return getLanguageName(preferredLanguageCode);
  }, [preferredLanguageCode, t]);

  const handleOpenLanguageModal = () => setIsLanguageModalOpen(true);
  const handleCloseLanguageModal = () => setIsLanguageModalOpen(false);
  const handleLanguageSelect = (language: Language) => {
    dispatch(updateLanguagePreference({ languageCode: language.code }));
    setIsLanguageModalOpen(false);
  };

  return (
    <Container>
      <Title>{t('profile.conversationSettings')}</Title>
      <SettingRow>
        <SettingLabel htmlFor="study-native-language-toggle">
          {t('profile.studyInNativeLanguage')}
        </SettingLabel>
        <StyledSwitch
          id="study-native-language-toggle"
          checked={studyInNativeLanguage}
          onChange={handleToggleChange}
          inputProps={{ 'aria-label': t('profile.studyInNativeLanguage') }}
        />
      </SettingRow>

      <SettingRow>
        <SettingLabel htmlFor="preferred-language">
          {t('profile.nativeLanguage')}
        </SettingLabel>
        <ActionsContainer>
          <LanguageValuePill aria-live="polite" id="preferred-language">
            {preferredLanguageName}
          </LanguageValuePill>
          <ChangeButton onClick={handleOpenLanguageModal} aria-label={t('profile.changeNativeLanguage')}>
            {t('common.change', 'Change')}
          </ChangeButton>
        </ActionsContainer>
      </SettingRow>

      <LanguageSelectionModal
        isOpen={isLanguageModalOpen}
        onLanguageSelect={handleLanguageSelect}
        onClose={handleCloseLanguageModal}
      />
    </Container>
  );
};

export default ConversationSettings;